// Revisado: 2026-02-21 [I18n Scan] - [Meta SEO]
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../constants';
import { ROUTES } from '../../routes';
import RaceSearch from './RaceSearch';
import FadeImage from '../shared/FadeImage';
import SEO from '../shared/SEO';
import LoadingScreen from '../shared/LoadingScreen';
import { createPlaceholderUrl } from '../../utils/PlaceholderCreator';
import { useTranslation } from 'react-i18next';
import { Ruler, RotateCw, RotateCcw, MoveRight, AlertCircle, Search } from 'lucide-react';
import RaceListHeader from './RaceListHeader';

const RaceList = () => {
    const { t } = useTranslation();
    const [races, setRaces] = useState([]);
    const [filteredRaces, setFilteredRaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch Races
    useEffect(() => {
        const fetchRaces = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/races`);
                if (!response.ok) {
                    throw new Error('Failed to fetch races');
                }
                const data = await response.json();
                setRaces(data);
                setFilteredRaces(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRaces();
    }, []);

    // Handle Filters
    const handleSearch = (filters) => {
        if (Object.keys(filters).length === 0) {
            setFilteredRaces(races);
            return;
        }

        const filtered = races.filter(race => {
            // Name Filter
            if (filters.name && !race.race_name.toLowerCase().includes(filters.name.toLowerCase())) {
                return false;
            }
            // Grade Filter
            if (filters.grade && String(race.grade) !== String(filters.grade)) {
                return false;
            }
            // Surface Filter
            if (filters.surface && String(race.course.surface) !== String(filters.surface)) {
                return false;
            }
            // Distance Type Filter
            if (filters.distanceType && String(race.course.distance_type) !== String(filters.distanceType)) {
                return false;
            }
            return true;
        });

        setFilteredRaces(filtered);
    };

    // Helper functions for styles (from Mockup)
    const getGradeColor = (grade) => {
        const g = String(grade).toUpperCase();
        if (g.includes("G1")) return "bg-blue-600 border-blue-400 text-white";
        if (g.includes("G2")) return "bg-pink-600 border-pink-400 text-white";
        if (g.includes("G3")) return "bg-green-600 border-green-400 text-white";
        if (g.includes("OP")) return "bg-orange-500 border-orange-300 text-white";
        return "bg-gray-600 border-gray-400 text-white";
    };

    const getTitleColorClass = (grade) => {
        const g = String(grade).toUpperCase();
        if (g.includes("G1")) return "text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400";
        if (g.includes("G2")) return "text-pink-600 dark:text-pink-400 border-pink-600 dark:border-pink-400";
        if (g.includes("G3")) return "text-green-600 dark:text-green-400 border-green-600 dark:border-green-400";
        return "text-gray-800 dark:text-white border-gray-400 dark:border-gray-500";
    };

    const getDirectionIcon = (direction) => {
        const d = String(direction).toLowerCase();
        if (d.includes("clock") && !d.includes("count")) return <RotateCw className="w-4 h-4" />;
        if (d.includes("count") || d.includes("left")) return <RotateCcw className="w-4 h-4" />;
        return <MoveRight className="w-4 h-4" />;
    };

    if (loading) return <LoadingScreen />;
    if (error) return <div className="text-center py-10 text-red-500">{t('RaceList.error', { error })}</div>;

    return (
        <div className="container mx-auto px-4 py-6 max-w-7xl">
            <SEO title={t('MetaRaceList.title')} description={t('MetaRaceList.description')} />
            {/* Header Section */}
            <RaceListHeader />

            <RaceSearch onSearch={handleSearch} />

            <div className="flex flex-col gap-4">
                {filteredRaces.length > 0 ? (
                    filteredRaces.map((race) => {
                        const gradeColor = getGradeColor(race.grade);
                        const titleColorClass = getTitleColorClass(race.grade);

                        return (
                            <Link
                                to={`${ROUTES.RACE}/${race.race_id}`}
                                key={race.race_id}
                                className="block bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 flex overflow-hidden group"
                            >
                                {/* Race Image */}
                                <div className="flex-shrink-0 w-[128px] bg-gray-100 dark:bg-gray-900 flex items-center justify-center relative overflow-hidden">
                                    <div className="relative w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-800">
                                        {(() => {
                                            const placeholderUrl = createPlaceholderUrl(128, 64, t('RaceList.placeholder_banner'), '#f3f4f6', '#9ca3af');
                                            const bannerSrc = race.banner_id
                                                ? `${API_BASE_URL}/api/thumb?img=/images/banner/races/banner_race_${race.banner_id}.png&width=128`
                                                : placeholderUrl;

                                            return (
                                                <FadeImage
                                                    src={bannerSrc}
                                                    alt={race.race_name}
                                                    className="object-contain"
                                                    fallback={placeholderUrl}
                                                    style={{ width: '128px', height: '64px' }}
                                                />
                                            );
                                        })()}
                                    </div>
                                    <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-bold shadow-sm border ${gradeColor} backdrop-blur-sm bg-opacity-90`}>
                                        {race.grade}
                                    </div>
                                </div>

                                {/* Info Section */}
                                <div className="p-4 flex-grow grid grid-cols-1 md:grid-cols-12 gap-4 items-center">

                                    {/* Main Details */}
                                    <div className="md:col-span-6 min-w-0 flex items-center">
                                        {/* Decorative Sidebar */}
                                        <div className={`h-10 w-1 rounded-full mr-3 hidden sm:block ${titleColorClass.split(' ').pop()}`}></div>

                                        <div className="min-w-0">
                                            {/* Title */}
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3
                                                    className={`font-extrabold text-xl md:text-2xl tracking-tight truncate leading-none ${titleColorClass.split(' ').shift()} ${titleColorClass.split(' ')[1]}`}
                                                    title={race.race_name}
                                                >
                                                    {race.race_name}
                                                </h3>
                                            </div>

                                            <div className="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap items-center gap-2">
                                                <span className="font-bold uppercase text-xs tracking-wide bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                                    {race.course.track_name}
                                                </span>
                                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                                <span className={String(race.course.surface).toLowerCase().includes('turf') ? 'text-green-600 dark:text-green-400 font-bold uppercase text-xs' : 'text-yellow-700 dark:text-yellow-500 font-bold uppercase text-xs'}>
                                                    {race.course.surface}
                                                </span>
                                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                                <span className="font-bold text-gray-700 dark:text-gray-200">{race.course.distance}m</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Technical Details: Styled with Icons */}
                                    <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-1 gap-3 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-3 md:pt-0 md:pl-6">
                                        {/* Type Row */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex-shrink-0 p-1.5 rounded-md bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
                                                <Ruler className="w-4 h-4" />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-0.5">{t('RaceList.label_type')}</span>
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-none">{race.course.distance_type}</span>
                                            </div>
                                        </div>

                                        {/* Direction Row */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex-shrink-0 p-1.5 rounded-md bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                                                {getDirectionIcon(race.course.direction)}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-0.5">{t('RaceList.label_direction')}</span>
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-none">{race.course.direction}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Schedule Section */}
                                    <div className="md:col-span-3 flex flex-col justify-center h-full">
                                        <p className="text-[10px] font-bold text-gray-400 mb-1.5 uppercase tracking-wider flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" /> {t('RaceList.header_schedules')}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {race.schedules && race.schedules.slice(0, 4).map((sched, idx) => (
                                                <span key={idx} className="inline-block px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded text-xs border border-blue-100 dark:border-blue-800 font-medium text-center flex-grow md:flex-grow-0">
                                                    {sched.year} {sched.month} {sched.cycle}
                                                </span>
                                            ))}
                                            {race.schedules && race.schedules.length > 4 && (
                                                <span className="text-xs text-gray-400 self-center">+{race.schedules.length - 4}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                        <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
                        <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">{t('RaceList.empty_state')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
export default RaceList;
